import { createContext, useContext } from "react";
import type { TableColumnLayout, TableColumnWidths, TableStructuralRowsColor, TableStructuralRowsText } from "./types";
import type { ColumnResizeHandleProps } from "./useColumnResize";

export type TableHeaderSelection = "none" | "some" | "all";

export interface TableContextValue {
  layoutById: Record<string, TableColumnLayout>;
  shares: TableColumnWidths;
  gridTemplateColumns: string;
  rowHeight: number;
  /** Pixel cap on an expand cell's height, or undefined when unbounded. */
  expandMaxHeight: number | undefined;
  hasExpandColumn: boolean;
  structuralRowsColor: TableStructuralRowsColor;
  structuralRowsText: TableStructuralRowsText;
  /** Whether a `Table.FooterRow` follows the body, so the last data row leaves the separator to it. */
  hasFooterRow: boolean;
  canSelect: boolean;
  canReorder: boolean;
  selectedRowIds: ReadonlySet<string>;
  headerSelection: TableHeaderSelection;
  onToggleRow?: (rowId: string) => void;
  onToggleAllRows?: () => void;
  /** Null when the column is fixed-width or the table is not resizable. */
  getResizeHandleProps: (columnId: string) => ColumnResizeHandleProps | null;
  resizingColumnId: string | null;
  /** Each column's rendered content width in pixels, measured once from the header. */
  columnContentWidths: Record<string, number>;
}

const TableContext = createContext<TableContextValue | null>(null);

export const TableContextProvider = TableContext.Provider;

export const useTableContext = (): TableContextValue => {
  const value = useContext(TableContext);
  if (!value) throw new Error("Table parts must be rendered inside <Table>");
  return value;
};
